import type { Ref, ComputedRef } from 'vue'
import { ref, computed, watch } from 'vue'
import { orderBy } from 'lodash-es'
import { useLocalStorage } from '@vueuse/core'
import type { File, Folder, Favorite, MiniFolder } from '@openapi'
import type { RecentFile } from '@lib/types/recentFiles.types'
import type { CompactFolder } from '@browser/stores/api'
import useApiStore from '@browser/stores/api'
import type { ViewCompactFile, ViewCompactFileFolder, ViewCompactFolder } from '@lib/types/files'
import { ViewCompactItemType } from '@lib/types/files'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'

export type AppName = 'admin' | 'impact'

export type AppCurrentState =
  | 'browse'
  | 'create-folder'
  | 'upload-files'
  | 'update-folder'
  | 'update-file'
  | 'batch-move'
export type BrowserSectionType = 'all' | 'favorites' | 'recent' | 'trash'
export type SortOption = 'nameAsc' | 'nameDesc' | 'modifiedAsc' | 'modifiedDesc'
export type StateSelectedItem = Partial<File | Folder | Favorite> & {
  type: string | null | undefined
  thumbnail_url: string | null | undefined
  content_type: string | null | undefined
}
type SelectedItemId =
  | Folder['id']
  | File['id']
  | RecentFile['id']
  | (Favorite['page'] extends { id: any } ? Favorite['page']['id'] : never)
type View = 'grid' | 'table'

type RecentFileWithFavoriteId = RecentFile & {
  favoriteId: string
}

type FavoriteExtended = Favorite & {
  favoriteId: string
  tags?: string[]
  content_type?: string | null | undefined
  type?: 'file' | 'folder'
}

interface FilteredByVisibilityItem {
  folder?: any
  name?: string | null
  type?: ViewCompactItemType | 'file' | 'folder'
  folders_count?: number
  files_count?: number
  available_folders_count?: number
  available_files_count?: number
  opacity?: number
}

function filterByItemVisibility(items: FilteredByVisibilityItem[]): FilteredByVisibilityItem[] {
  const isAdmin = state.appName.value === 'admin'
  const isSearchMode = state.showFilteredResults.value

  return items.reduce((acc, item) => {
    const hasCountData =
      'files_count' in item ||
      'folders_count' in item ||
      'available_files_count' in item ||
      'available_folders_count' in item

    const filesCount = item.available_files_count ?? item.files_count ?? 0
    const foldersCount = item.available_folders_count ?? item.folders_count ?? 0

    const isFolderEmpty = item.type == 'folder' && hasCountData && filesCount === 0 && foldersCount === 0
    const isFolderWithoutData = item.type == 'folder' && !hasCountData && isSearchMode

    const shouldHideItem = item.name?.startsWith('.') || isFolderEmpty || item.folder?.file_thumbnail_urls?.length === 0

    if ((!shouldHideItem && !isAdmin) || isAdmin) {
      acc.push({
        ...item,
        opacity: isFolderWithoutData || (shouldHideItem && isAdmin) ? 0.5 : 1,
      })
    }
    return acc
  }, [] as FilteredByVisibilityItem[])
}

function sortItems<T extends { name?: string; modified_at?: string }>(items: T[], sortOption: SortOption): T[] {
  switch (sortOption) {
    case 'nameAsc':
    case 'nameDesc':
      return [...items].sort((a, b) => {
        const comparison = (a.name || '').localeCompare(b.name || '')
        return sortOption === 'nameAsc' ? comparison : -comparison
      })
    case 'modifiedAsc':
    case 'modifiedDesc':
      return [...items].sort((a, b) => {
        const dateA = a.modified_at ? new Date(a.modified_at).getTime() : 0
        const dateB = b.modified_at ? new Date(b.modified_at).getTime() : 0
        return sortOption === 'modifiedAsc' ? dateA - dateB : dateB - dateA
      })
    default:
      return items
  }
}

interface State {
  appName: Ref<AppName | ''>
  currentState: Ref<AppCurrentState>
  selectedItemIds: Ref<SelectedItemId[]>
  selectedSection: Ref<BrowserSectionType>
  selectedView: Ref<View>
  selectedSortOption: Ref<SortOption>
  search: Ref<string>
  displayedFiles: ComputedRef<ViewCompactFile[]>
  displayedFolders: ComputedRef<ViewCompactFolder[]>
  displayedFilesFolders: ComputedRef<ViewCompactFileFolder[]>
  sortedFavorites: ComputedRef<Favorite[]>
  displayedFavorites: ComputedRef<FavoriteExtended[]>
  filteredRecentFiles: ComputedRef<RecentFileWithFavoriteId[]>
  displayedRecentFiles: ComputedRef<RecentFileWithFavoriteId[]>
  isContentPanelOpen: Ref<boolean>
  updatingFolderId: Ref<Folder['id'] | null>
  selectedFileId: ComputedRef<File['id'] | null>
  selectedFolderId: ComputedRef<Folder['id'] | null>
  updatingFolder: ComputedRef<MiniFolder | null>
  inlineEditingItem: Ref<{ id: string | number; prop: 'name' | 'tags' } | null>
  showFilteredResults: ComputedRef<boolean>
  isAdmin: ComputedRef<boolean>
  enabledFavorites: ComputedRef<boolean>
  enabledRecents: ComputedRef<boolean>
  disabledSearchContext: ComputedRef<boolean>
  isLocalSearch: Ref<boolean>
  isMobileView: Ref<boolean>
  pitcherSettings: Ref<OrganizationSettings & InstanceSettings>
}

const state: State = {
  appName: ref(''),
  currentState: ref('browse'),
  selectedItemIds: ref([]),
  selectedSection: ref('all'),
  selectedSortOption: useLocalStorage<SortOption>('selectedSortOption', 'nameAsc', {
    listenToStorageChanges: true,
  }),
  search: ref(''),
  pitcherSettings: ref({}),
  isMobileView: ref(false),
  isLocalSearch: useLocalStorage<boolean>('isContentBrowserLocalSearch', false, {
    listenToStorageChanges: true,
  }),
  selectedView: useLocalStorage<View>('selectedView', 'table', {
    listenToStorageChanges: true,
  }),
  isContentPanelOpen: ref(false),
  updatingFolderId: ref(null),
  inlineEditingItem: ref(null),
  showFilteredResults: computed(() => {
    const { state: apiState } = useApiStore()

    return !!apiState.searchQuery || Object.keys(apiState.searchMetadataFilters).length > 0
  }),
  isAdmin: computed(() => state.appName.value === 'admin'),
  enabledFavorites: computed(() => state.pitcherSettings.value.enable_favorites ?? false),
  enabledRecents: computed(() => state.pitcherSettings.value.enable_recents ?? false),
  disabledSearchContext: computed(() => state.pitcherSettings.value.disable_browser_search_context ?? false),
  displayedFiles: computed(() => {
    const { state: apiState, getters: apiGetters } = useApiStore()

    const sourceFiles = state.showFilteredResults.value
      ? apiState.filteredItems.filter((i) => (i as any).type === 'file')
      : apiGetters.sortedFiles

    const files = sourceFiles.map((file) => {
      const { created_by, tags, ...remainingFileProps } = file
      return {
        ...remainingFileProps,
        tags: tags ? [...tags] : undefined,
        type: ViewCompactItemType.File,
        createdBy: created_by?.name ?? '-',
        favoriteId: apiState.favorites[file.id]?.id,
      } as ViewCompactFile
    })

    return sortItems(files, state.selectedSortOption.value)
  }),
  displayedFolders: computed(() => {
    const { state: apiState } = useApiStore()

    const sourceFolders = state.showFilteredResults.value
      ? apiState.filteredItems.filter((i) => (i as any).type === 'folder')
      : apiState.folders

    const folders = sourceFolders.reduce((acc: ViewCompactFolder[], folder) => {
      if (folder.name?.toLocaleLowerCase().includes(apiState.searchQuery.toLocaleLowerCase())) {
        acc.push({
          ...(folder as CompactFolder),
          type: ViewCompactItemType.Folder,
          favoriteId: apiState.favorites[folder.id]?.id,
        })
      }
      return acc
    }, [])

    return sortItems(folders, state.selectedSortOption.value)
  }),
  displayedFilesFolders: computed(
    () =>
      filterByItemVisibility([
        ...state.displayedFolders.value,
        ...state.displayedFiles.value,
      ]) as (ViewCompactFileFolder & { opacity?: number })[],
  ),
  sortedFavorites: computed(() => {
    const folderComparator = (i: Favorite) => !!i.folder
    const fileComparator = (i: Favorite) => !!i.file && !i.page
    const pageComparator = (i: Favorite) => !!i.file && !!i.page
    const nameComparator = (i: Favorite) => i.name?.toLocaleLowerCase()

    const { state: apiState } = useApiStore()

    const favorites = filterByItemVisibility(Object.values(apiState.favorites))

    return orderBy(
      favorites as Favorite[],
      [folderComparator, fileComparator, pageComparator, nameComparator],
      ['desc', 'desc', 'desc', 'asc'],
    ).map((item) => ({
      ...item,
      tags: item?.file?.tags ?? [],
      type: item?.file ? 'file' : 'folder',
      content_type: item?.file?.content_type,
    })) as FavoriteExtended[]
  }),
  displayedFavorites: computed(() => {
    const { state: apiState } = useApiStore()

    return state.sortedFavorites.value.reduce((acc, fav) => {
      const id = fav.page?.id ?? fav.file?.id ?? fav.folder?.id

      const searchQueryLower = apiState.searchQuery.toLocaleLowerCase()
      const matchesSearchQuery = id && fav.name?.toLocaleLowerCase().includes(searchQueryLower)

      if (matchesSearchQuery) {
        acc.push({
          ...fav,
          favoriteId: fav.id,
          id: id,
        })
      }
      return acc
    }, [] as FavoriteExtended[])
  }),
  filteredRecentFiles: computed(() => {
    const { state: apiState } = useApiStore()
    return filterByItemVisibility(
      Object.values(apiState.recent).map((r) => ({
        ...r,
        favoriteId: apiState.favorites[r.id]?.id,
        type: ViewCompactItemType.File,
      })),
    ) as RecentFileWithFavoriteId[]
  }),
  displayedRecentFiles: computed(() => {
    const { state: apiState } = useApiStore()

    return state.filteredRecentFiles.value.reduce((acc, recent) => {
      const id = recent.id

      const searchQueryLower = apiState.searchQuery.toLocaleLowerCase()
      const matchesSearchQuery = id && recent.name?.toLocaleLowerCase().includes(searchQueryLower)

      if (matchesSearchQuery) {
        acc.push(recent)
      }
      return acc
    }, [] as RecentFileWithFavoriteId[])
  }),

  selectedFileId: computed(() => {
    if (state.selectedItemIds.value.length !== 1) return null
    const selectedItemId = state.selectedItemIds.value[0]?.split('|')[0]

    if (state.selectedSection.value === 'all') {
      return state.displayedFiles.value.find((f) => f.id === selectedItemId)?.id ?? null
    } else if (state.selectedSection.value === 'favorites') {
      return state.displayedFavorites.value.find((f) => f.file?.id === selectedItemId)?.id ?? null
    } else if (state.selectedSection.value === 'recent') {
      return state.displayedRecentFiles.value.find((f) => f.id === selectedItemId)?.id ?? null
    }

    return null
  }),
  selectedFolderId: computed(() => {
    const { state: apiState } = useApiStore()

    if (state.selectedItemIds.value.length !== 1) return null
    const selectedItemId = state.selectedItemIds.value[0]

    if (state.selectedSection.value === 'all') {
      // Check if search from Everywhere
      if (apiState.searchQuery && !state.isLocalSearch.value) {
        return apiState.filteredItems.find((f) => f.id === selectedItemId)?.id ?? null
      }
      return apiState.folders.find((f) => f.id === selectedItemId)?.id ?? null
    } else if (state.selectedSection.value === 'favorites') {
      const selectedFavorite = apiState.favorites[selectedItemId]
      return selectedFavorite?.folder?.id ?? null
    }
    // no folders in recent files, hence no check

    return null
  }),
  updatingFolder: computed(() => {
    const { state: apiState } = useApiStore()

    return state.updatingFolderId.value
      ? (apiState.folders.find((f) => f.id === state.updatingFolderId.value) ?? null)
      : null
  }),
}

const actions = {
  setAppName(appName: AppName): void {
    state.appName.value = appName
  },
  setSortOption(sortOption: SortOption): void {
    state.selectedSortOption.value = sortOption
  },
  resetSelectedItems(): void {
    state.selectedItemIds.value = []
  },
  removeSelectedItems(removeIds: SelectedItemId[]): void {
    const removeIdsSet = new Set(removeIds)
    state.selectedItemIds.value = state.selectedItemIds.value.filter((id) => !removeIdsSet.has(id))
  },
  selectAll(): void {
    state.selectedItemIds.value = state.displayedFilesFolders.value.map((item) => item.id)
  },
  toggleSelectAll(value: boolean) {
    if (state.selectedSection.value === 'all') {
      state.selectedItemIds.value = value ? state.displayedFilesFolders.value.map((item) => item.id) : []
    } else if (state.selectedSection.value === 'favorites') {
      state.selectedItemIds.value = value ? state.displayedFavorites.value.map((item) => item.id) : []
    } else if (state.selectedSection.value === 'recent') {
      state.selectedItemIds.value = value ? state.displayedRecentFiles.value.map((item) => item.id) : []
    }
  },
  toggleItemSelection(
    item: StateSelectedItem,
    { isSelected, disableMultiple }: { isSelected?: boolean; disableMultiple?: boolean } = {},
  ): void {
    const index = state.selectedItemIds.value.findIndex((id) => id === item.id)

    if (index >= 0) {
      if (isSelected === true) return
      disableMultiple ? (state.selectedItemIds.value = []) : state.selectedItemIds.value.splice(index, 1)
    } else if (item.id) {
      if (isSelected === false) return
      disableMultiple ? (state.selectedItemIds.value = [item.id]) : state.selectedItemIds.value.push(item.id)
    } else {
      console.warn('Cannot toggle selection for item without id', item)
    }
  },
  toggleSingleItemSelection(item: Partial<File | Folder | Favorite>): void {
    const index = state.selectedItemIds.value.findIndex((id) => id === item.id)

    if (index >= 0) {
      state.selectedItemIds.value = []
    } else if (item.id) {
      state.selectedItemIds.value = [item.id]
    } else {
      console.warn('Cannot toggle selection for item without id', item)
    }
  },
  toggleItemFavorite(item: ViewCompactFileFolder & { favoriteId?: string }): void {
    const { state: apiState } = useApiStore()

    if (!item.favoriteId) {
      const key = {
        [ViewCompactItemType.File]: 'file_id',
        [ViewCompactItemType.Folder]: 'folder_id',
      }[item.type]

      apiState.favoriteCreator?.({ [key]: item.id })
    } else {
      apiState.favoriteDeleter?.(item.favoriteId)
    }
  },

  setSelectedSection(section: BrowserSectionType, folderId?: string): void {
    const { state: apiState } = useApiStore()

    function clearState() {
      state.selectedItemIds.value = []
    }

    function fetchDataPerSection() {
      switch (section) {
        case 'favorites':
          apiState.favoritesFetcher?.()
          break

        case 'recent':
          apiState.recentFilesFetcher?.()
          if (isRootFolderWithoutId) apiState.favoritesFetcher?.()
          break

        case 'all':
          apiState.folderItemsFetcher?.(folderId)
          apiState.recentFilesFetcher?.()
          break

        default:
          console.warn('Unknown file browser section:', section)
      }
    }

    const sectionChanged = section !== state.selectedSection.value
    const folderIdChanged = section === 'all' && folderId !== apiState.currentFolderId && !!folderId
    const isRootFolderWithoutId = apiState.currentFolderId === '' || folderId === undefined

    if (sectionChanged) {
      state.selectedSection.value = section
      clearState()
      fetchDataPerSection()
    } else if (isRootFolderWithoutId && (state.isAdmin.value || section === 'all')) {
      fetchDataPerSection()
      apiState.favoritesFetcher?.()
    } else if (folderIdChanged) {
      clearState()
      apiState.folderItemsFetcher?.(folderId)
    }

    if (section === 'all' && state.isAdmin.value) {
      //Note: after closing file viewer we need to fetch the recent files to update recent tab
      apiState.recentFilesFetcher?.()
    }
  },
  toggleSelectedView(): void {
    state.selectedView.value = state.selectedView.value === 'grid' ? 'table' : 'grid'
  },
  setSearch(search: string): void {
    state.search.value = search
  },
  setIsLocalSearch(isLocal: boolean): void {
    state.isLocalSearch.value = isLocal
  },
  toggleContentPanel(isOpen?: boolean): void {
    state.isContentPanelOpen.value = isOpen ?? !state.isContentPanelOpen.value
  },
  selectAndOpenContentPanel(item: ViewCompactFileFolder): void {
    actions.resetSelectedItems()
    actions.toggleItemSelection(item as StateSelectedItem)
    actions.toggleContentPanel(true)
  },
  setCurrentState(stateName: AppCurrentState, itemId?: Folder['id'] | File['id']): void {
    state.currentState.value = stateName
    state.updatingFolderId.value = stateName === 'update-folder' ? (itemId as Folder['id']) : null
  },
  clearInlineEditingItem(): void {
    state.inlineEditingItem.value = null
  },
}

// Disable local search when browser search context is disabled
watch(
  state.disabledSearchContext,
  (isSearchContextDisabled) => {
    if (isSearchContextDisabled) {
      state.isLocalSearch.value = false
    }
  },
  { immediate: true },
)

export default function () {
  return { ...state, ...actions }
}
